import { useLang } from "@/context/LanguageContext";
import { ASSETS } from "@/i18n";
import { Reveal } from "@/components/Reveal";
import {
  CalendarDays,
  Users,
  Sparkles,
  Package,
  ScanFace,
  Smartphone,
  FileText,
  ScanLine,
  Palette,
  Megaphone,
} from "lucide-react";

export const Features = () => {
  const { t } = useLang();
  const items = t.features.items;

  return (
    <section
      id="features"
      data-testid="features-section"
      className="relative py-24 md:py-32 bg-[#FAFAFA]"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <Reveal>
          <div className="max-w-2xl">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#A78BFA] mb-5" data-testid="features-eyebrow">
              {t.features.eyebrow}
            </div>
            <h2
              data-testid="features-title"
              className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[#1A1A1A]"
            >
              {t.features.title}{" "}
              <span className="italic text-[#737373]">{t.features.title_accent}</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-[#4A4A4A] leading-relaxed font-light max-w-xl" data-testid="features-subtitle">
              {t.features.subtitle}
            </p>
          </div>
        </Reveal>

        {/* Bento grid */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 gap-5 auto-rows-[200px]">
          {/* Agenda smart — image + text */}
          <ImageFeatureCard
            testId="feature-agenda"
            delay={0}
            className="md:col-span-4 lg:col-span-7 row-span-2"
            image={ASSETS.agenda}
            icon={<CalendarDays size={18} />}
            title={items.agenda.title}
            desc={items.agenda.desc}
            tone="purple"
            large
          />

          {/* Skin AI — facial image bg */}
          <ImageFeatureCard
            testId="feature-skin"
            delay={120}
            className="md:col-span-2 lg:col-span-5 row-span-2"
            image={ASSETS.facial}
            icon={<ScanFace size={18} />}
            title={items.skin.title}
            desc={items.skin.desc}
            badge="AI"
            tone="cyan"
            large
          />

          {/* Clienti */}
          <ImageFeatureCard
            testId="feature-clienti"
            delay={0}
            className="md:col-span-3 lg:col-span-4 row-span-2"
            image={ASSETS.clienti}
            icon={<Users size={18} />}
            title={items.clienti.title}
            desc={items.clienti.desc}
            tone="purple"
          />

          {/* Trattamenti */}
          <ImageFeatureCard
            testId="feature-trattamenti"
            delay={120}
            className="md:col-span-3 lg:col-span-4 row-span-2"
            image={ASSETS.trattamenti}
            icon={<Sparkles size={18} />}
            title={items.trattamenti.title}
            desc={items.trattamenti.desc}
            tone="dark"
          />

          {/* Magazzino */}
          <ImageFeatureCard
            testId="feature-magazzino"
            delay={240}
            className="md:col-span-3 lg:col-span-4 row-span-2"
            image={ASSETS.magazzino}
            icon={<Package size={18} />}
            title={items.magazzino.title}
            desc={items.magazzino.desc}
            tone="cyan"
          />

          {/* Bolle OCR — designed AI scanner card */}
          <Reveal className="md:col-span-3 lg:col-span-7 row-span-2" delay={0}>
            <BolleCard t={items.bolle} />
          </Reveal>

          {/* Nail rendering — uses hands image */}
          <ImageFeatureCard
            testId="feature-nails"
            delay={120}
            className="md:col-span-3 lg:col-span-5 row-span-2"
            image={ASSETS.hands}
            icon={<Palette size={18} />}
            title={items.nails.title}
            desc={items.nails.desc}
            badge={items.nails.badge}
            tone="purple"
            large
          />

          {/* Fidelity — custom designed card with AURA logo */}
          <Reveal className="md:col-span-3 lg:col-span-3 row-span-2" delay={0}>
            <FidelityCard t={items.fidelity} />
          </Reveal>

          {/* App mobile — image overlay */}
          <ImageFeatureCard
            testId="feature-mobile"
            delay={120}
            className="md:col-span-3 lg:col-span-3 row-span-2"
            image={ASSETS.phone}
            icon={<Smartphone size={18} />}
            title={items.mobile.title}
            desc={items.mobile.desc}
            tone="dark"
          />

          {/* Preventivi */}
          <ImageFeatureCard
            testId="feature-preventivi"
            delay={240}
            className="md:col-span-3 lg:col-span-3 row-span-2"
            image={ASSETS.preventivi}
            icon={<FileText size={18} />}
            title={items.preventivi.title}
            desc={items.preventivi.desc}
            tone="purple"
          />

          {/* Marketing & AI */}
          <ImageFeatureCard
            testId="feature-marketing"
            delay={360}
            className="md:col-span-3 lg:col-span-3 row-span-2"
            image={ASSETS.marketing}
            icon={<Megaphone size={18} />}
            title={items.marketing.title}
            desc={items.marketing.desc}
            badge={items.marketing.badge}
            tone="purple"
          />
        </div>
      </div>
    </section>
  );
};

const FeatureCard = ({ testId, className = "", icon, title, desc, large = false, badge, delay = 0 }) => (
  <Reveal className={className} delay={delay}>
    <div
      data-testid={testId}
      className={`relative h-full rounded-3xl border border-black/5 p-7 md:p-8 flex flex-col justify-between tilt-card ${className}`}
    >
      <div className="flex items-start justify-between">
        <div className="h-11 w-11 rounded-2xl bg-white shadow-sm border border-black/5 flex items-center justify-center">
          {icon}
        </div>
        {badge && (
          <span className="text-[10px] tracking-[0.25em] uppercase bg-[#1A1A1A] text-white px-2 py-1 rounded-full">
            {badge}
          </span>
        )}
      </div>
      <div>
        <h3 className={`font-serif tracking-tight text-[#1A1A1A] ${large ? "text-3xl md:text-4xl" : "text-2xl"}`}>
          {title}
        </h3>
        <p className={`mt-2 text-[#4A4A4A] font-light leading-relaxed ${large ? "text-base md:text-lg" : "text-sm"}`}>
          {desc}
        </p>
      </div>
    </div>
  </Reveal>
);

/** Card with full-bleed image + dark gradient overlay and overlayed text */
const ImageFeatureCard = ({
  testId,
  className = "",
  image,
  icon,
  title,
  desc,
  badge,
  large = false,
  delay = 0,
  tone = "dark", // 'dark' | 'purple' | 'cyan'
}) => {
  const overlay =
    tone === "purple"
      ? "from-[#1A0B2E]/90 via-[#3B1F73]/40 to-transparent"
      : tone === "cyan"
      ? "from-[#0B1F2E]/90 via-[#1B5775]/30 to-transparent"
      : "from-black/85 via-black/40 to-transparent";

  return (
    <Reveal className={className} delay={delay}>
      <div
        data-testid={testId}
        className="relative h-full rounded-3xl overflow-hidden border border-black/5 group tilt-card"
      >
        <img
          src={image}
          alt=""
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1400ms]"
        />
        <div className={`absolute inset-0 bg-gradient-to-t ${overlay}`} />
        {badge && (
          <span className="absolute top-5 right-5 text-[10px] tracking-[0.25em] uppercase bg-white/15 backdrop-blur text-white border border-white/20 px-2.5 py-1 rounded-full">
            {badge}
          </span>
        )}
        <div className="relative h-full p-7 md:p-8 flex flex-col justify-between text-white">
          <div className="h-11 w-11 rounded-2xl bg-white/15 backdrop-blur border border-white/20 flex items-center justify-center text-white">
            {icon}
          </div>
          <div>
            <h3 className={`font-serif tracking-tight ${large ? "text-3xl md:text-4xl" : "text-2xl"}`}>
              {title}
            </h3>
            <p className={`mt-2 font-light leading-relaxed text-white/85 ${large ? "text-base md:text-lg" : "text-sm"}`}>
              {desc}
            </p>
          </div>
        </div>
      </div>
    </Reveal>
  );
};

/** Custom card with AURA logo styled as a digital "fidelity / gift" card */
const FidelityCard = ({ t }) => (
  <div
    data-testid="feature-fidelity"
    className="relative h-full rounded-3xl overflow-hidden border border-black/5 tilt-card flex flex-col justify-between p-7 md:p-8"
    style={{
      background:
        "linear-gradient(135deg, #1A1A1A 0%, #2A1F3D 55%, #1A1A1A 100%)",
    }}
  >
    {/* decorative animated blobs */}
    <div
      aria-hidden
      className="pointer-events-none absolute -top-12 -right-12 h-44 w-44 rounded-full blur-3xl opacity-50 animate-blob"
      style={{ background: "radial-gradient(circle, #A78BFA 0%, transparent 70%)" }}
    />
    <div
      aria-hidden
      className="pointer-events-none absolute -bottom-16 -left-10 h-40 w-40 rounded-full blur-3xl opacity-45 animate-blob-slow"
      style={{ background: "radial-gradient(circle, #67E8F9 0%, transparent 70%)" }}
    />

    {/* Top row: AURA logo + chip + brand line */}
    <div className="relative flex items-start justify-between">
      <div className="flex items-center gap-3">
        <img
          src={ASSETS.logo}
          alt="AURA"
          className="h-12 w-12 rounded-full object-contain bg-white/10 border border-white/15"
        />
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/60">Fidelity Card</div>
          <div className="font-serif italic text-xl text-white">AURA</div>
        </div>
      </div>
      <div
        aria-hidden
        className="h-9 w-12 rounded-md border border-white/20"
        style={{
          background:
            "linear-gradient(135deg, #C4B5FD 0%, #67E8F9 50%, #FDBA74 100%)",
          opacity: 0.85,
        }}
      />
    </div>

    {/* Bottom: title + desc */}
    <div className="relative">
      <h3 className="font-serif tracking-tight text-2xl text-white">{t.title}</h3>
      <p className="mt-2 text-sm font-light leading-relaxed text-white/80">{t.desc}</p>

      <div className="mt-5 flex items-center justify-between text-[10px] tracking-[0.3em] uppercase text-white/50">
        <span>•••• 2026</span>
        <span className="bg-gradient-to-r from-[#A78BFA] to-[#67E8F9] bg-clip-text text-transparent font-medium">
          AURA REWARDS
        </span>
      </div>
    </div>
  </div>
);

/** Custom card showing OCR/AI scanning a delivery note that updates inventory */
const BolleCard = ({ t }) => (
  <div
    data-testid="feature-bolle"
    className="relative h-full rounded-3xl overflow-hidden border border-black/5 tilt-card grid grid-cols-1 sm:grid-cols-5 gap-0 bg-gradient-to-br from-[#F5F0FF] via-[#FFFFFF] to-[#ECFEFF]"
  >
    {/* Left: text */}
    <div className="sm:col-span-3 p-7 md:p-9 flex flex-col justify-between relative z-10">
      <div className="flex items-start justify-between">
        <div className="h-11 w-11 rounded-2xl bg-white shadow-sm border border-black/5 flex items-center justify-center">
          <ScanLine size={22} className="text-[#7C3AED]" />
        </div>
        <span className="text-[10px] tracking-[0.25em] uppercase bg-[#1A1A1A] text-white px-2.5 py-1 rounded-full">
          {t.badge}
        </span>
      </div>
      <div className="mt-6">
        <h3 className="font-serif tracking-tight text-3xl md:text-4xl text-[#1A1A1A]">{t.title}</h3>
        <p className="mt-3 text-base font-light leading-relaxed text-[#4A4A4A] max-w-md">{t.desc}</p>
        <div className="mt-5 flex flex-wrap gap-2">
          {[t.tag1, t.tag2, t.tag3].map((tag, i) => (
            <span
              key={i}
              className="text-[10px] tracking-[0.2em] uppercase bg-white border border-black/5 text-[#4A4A4A] rounded-full px-3 py-1"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>

    {/* Right: animated paper invoice visual */}
    <div className="sm:col-span-2 relative min-h-[200px] flex items-center justify-center p-6">
      <div
        aria-hidden
        className="absolute -top-10 -right-10 h-44 w-44 rounded-full blur-3xl opacity-60 animate-blob"
        style={{ background: "radial-gradient(circle, #C4B5FD 0%, transparent 70%)" }}
      />
      {/* Mock invoice card */}
      <div className="relative bg-white rounded-2xl shadow-[0_20px_40px_-15px_rgba(124,58,237,0.35)] border border-black/5 px-5 py-4 w-full max-w-[220px] rotate-[-4deg]">
        <div className="flex items-center justify-between text-[9px] tracking-[0.25em] uppercase text-[#737373]">
          <span>D.D.T.</span>
          <span>n° 2384</span>
        </div>
        <div className="mt-3 space-y-1.5">
          <InvoiceRow label="Crema notte 50ml" qty="× 12" />
          <InvoiceRow label="Olio corpo 100ml" qty="× 6" />
          <InvoiceRow label="Smalto rosa #14" qty="× 24" />
          <InvoiceRow label="Maschera lifting" qty="× 8" />
        </div>
        {/* AI scan line */}
        <div className="absolute left-0 right-0 h-[2px] aura-scan-line" />
      </div>
      {/* Result chip */}
      <div className="absolute bottom-4 right-4 sm:bottom-6 sm:right-6 inline-flex items-center gap-2 bg-[#1A1A1A] text-white text-[10px] tracking-[0.25em] uppercase px-3 py-2 rounded-full shadow-lg animate-float">
        <span className="h-1.5 w-1.5 rounded-full bg-[#67E8F9]" /> +50 in magazzino
      </div>
    </div>
  </div>
);

const InvoiceRow = ({ label, qty }) => (
  <div className="flex items-center justify-between text-[11px] text-[#1A1A1A] border-b border-dashed border-black/5 pb-1.5 last:border-b-0">
    <span className="font-light">{label}</span>
    <span className="font-medium">{qty}</span>
  </div>
);

